import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { auth, db, rtdb } from "../firebaseConfig";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from "firebase/firestore";
import { ref, set, onDisconnect, serverTimestamp as rtdbTimestamp } from "firebase/database";
import { markAgentLogout } from "../hooks/useTimeTracking";
import { initializeNotifications } from "../pwa/notificationPermission";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Calculate days remaining until a target date
 */
function calculateDaysUntil(targetDate) {
  if (!targetDate) return null;
  const target = targetDate instanceof Date ? targetDate : new Date(targetDate);
  const now = new Date();
  const diffMs = target - now;
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [client, setClient] = useState(null);
  const [roles, setRoles] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Admin tier preview override (sessionStorage-backed) — shown only in Learner view
  const [tierPreview, setTierPreviewState] = useState(() => {
    try { return sessionStorage.getItem("scope180-tier-preview") || null; } catch { return null; }
  });

  /* ================= 1. LOGOUT ACTION ================= */
  const logout = useCallback(async (redirectTo = "/") => {
    try {
      // Mark agent logout for grace period (before signOut invalidates auth)
      await markAgentLogout(auth.currentUser?.uid);
      localStorage.setItem("force-logout", String(Date.now()));
      const keysToClear = ["selectedProduct", "authUser", "redirectAfterLogin", "lastVisitedRoute"];
      keysToClear.forEach((k) => localStorage.removeItem(k));
      await signOut(auth);
    } catch (e) {
      console.error("Logout error", e);
    } finally {
      window.location.href = redirectTo;
    }
  }, []);

  /* ================= 2. CROSS-TAB SYNC ================= */
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "force-logout" && auth.currentUser) {
        signOut(auth).then(() => {
          window.location.href = "/";
        });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /* ================= 3. STABLE AUTH OBSERVER ================= */
  /**
   * FIX FOR ID: ca9: We have completely removed onSnapshot.
   * We now use a sequential async pattern:
   * 1. Auth State Changes
   * 2. If user, fetch Profile ONCE via getDoc
   * 3. Set all states
   * 4. Set loading to false only after everything is finished.
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      // Start loading sequence
      setLoading(true);

      if (user) {
        setCurrentUser(user);
        try {
          const userRef = doc(db, "users", user.uid);
          
          // ONE-TIME FETCH (Resolves the assertion error crash)
          const snap = await getDoc(userRef);
          
          if (snap.exists()) {
            const data = snap.data();
            setClient(data);

            // Resolve Roles/Admin Status
            const resolvedRoles = Array.isArray(data.roles)
              ? data.roles.map(r => String(r).toLowerCase())
              : [String(data.role || "learner").toLowerCase()];

            setRoles(resolvedRoles);
            setIsAdmin(resolvedRoles.includes("admin") || data.isAdmin === true);

            // Update login + access timestamps (non-blocking)
            // lastLoginAt: most recent auth sign-in (used by TheScopeAI)
            // lastActiveAt: most recent on-site activity (used by TheScopeAI for "last on site")
            // lastAccessedAt: legacy alias kept for backward compat
            updateDoc(userRef, {
              lastLoginAt: serverTimestamp(),
              lastActiveAt: serverTimestamp(),
              lastAccessedAt: serverTimestamp(),
            }).catch(() => {});

            // Initialize push notifications (non-blocking)
            const userRoleForNotif = (Array.isArray(data.roles) ? data.roles[0] : data.role) || "learner";
            initializeNotifications(user.uid, userRoleForNotif).catch(() => {});

            // ── RTDB Presence Tracking ──
            // Write logged-in user to RTDB so admin/live-support can see who's online
            try {
              const presenceRef = ref(rtdb, `visitors/${user.uid}`);
              const presenceData = {
                visitorId: user.uid,
                userId: user.uid,
                userName: data.fullName || data.name || user.displayName || user.email?.split("@")[0] || "User",
                userEmail: user.email || null,
                userRole: (Array.isArray(data.roles) ? data.roles[0] : data.role) || "learner",
                isOnline: true,
                page: window.location.pathname,
                lastSeen: Date.now(),
                sessionStart: Date.now(),
                userAgent: navigator.userAgent.substring(0, 150),
                referrer: document.referrer.substring(0, 200) || "direct",
              };
              set(presenceRef, presenceData).catch(() => {});
              // Auto-remove on disconnect
              onDisconnect(presenceRef).update({ isOnline: false, lastSeen: Date.now() }).catch(() => {});
              // Heartbeat every 30s
              const heartbeat = setInterval(() => {
                set(presenceRef, { ...presenceData, page: window.location.pathname, lastSeen: Date.now() }).catch(() => {});
              }, 30000);
              // Track page changes
              const trackPage = () => {
                set(presenceRef, { ...presenceData, page: window.location.pathname, lastSeen: Date.now() }).catch(() => {});
              };
              window.addEventListener("popstate", trackPage);
              // Cleanup on logout
              window.__scope180PresenceCleanup = () => {
                clearInterval(heartbeat);
                window.removeEventListener("popstate", trackPage);
                set(presenceRef, { ...presenceData, isOnline: false, lastSeen: Date.now() }).catch(() => {});
              };
            } catch (presErr) {
              console.warn("Presence tracking failed:", presErr);
            }
          } else {
            // Auto-recovery: user is authenticated but has no Firestore doc.
            // This can happen if registration failed partially (auth created, doc not).
            console.warn("No Firestore doc for authenticated user — creating recovery doc:", user.uid);
            const recoveryData = {
              uid: user.uid,
              email: user.email || "",
              fullName: user.displayName || user.email?.split("@")[0] || "",
              name: user.displayName || user.email?.split("@")[0] || "",
              role: "learner",
              subRole: null,
              testerMode: false,
              testerNda: { signed: false, declined: false },
              testerFeedbackCount: 0,
              testerAccessRevoked: false,
              subscription: { status: "none" },
              badges: { achievements: [] },
              registeredDate: new Date().toISOString().split("T")[0],
              createdAt: serverTimestamp(),
              recoveredAt: serverTimestamp(),
              userCourses: [],
              emailsReceived: [],
            };
            try {
              await setDoc(userRef, recoveryData);
              console.log("Recovery doc created for:", user.uid);

              setClient(recoveryData);
              setRoles(["learner"]);
              setIsAdmin(false);
            } catch (docErr) {
              console.error("Failed to create recovery doc:", docErr);
            }
          }

        } catch (err) {
          console.error("Auth profile fetch failed:", err);
        }
      } else {
        // Clear presence on logout
        if (window.__scope180PresenceCleanup) {
          window.__scope180PresenceCleanup();
          window.__scope180PresenceCleanup = null;
        }
        // Clear everything on logout
        setCurrentUser(null);
        setClient(null);
        setRoles([]);
        setIsAdmin(false);
      }

      // Finish loading sequence
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Idle sign-out is handled by IdleSignout component in App.js (with warning modal)

  // Compute subscription state from client data (Free / Premium)
  const subscription = useMemo(() => {
    // Check both nested and root-level subscription fields
    const nestedStatus = client?.subscription?.status;
    const rootStatus = client?.subscriptionStatus;
    // Only Free and Premium are offered; any legacy paid tier counts as Premium
    const rawTier = client?.subscriptionTier || "free";
    const tier = ["professional", "enterprise"].includes(rawTier) ? "premium" : rawTier;

    // Determine effective status
    const status = nestedStatus || rootStatus || "none";
    const trialEndDate = client?.subscription?.trialEndDate;

    // Paid tier means active subscription — isPremium is true for premium tier
    const isPaidTier = tier === "premium";
    const isPremium = isPaidTier;
    const isActiveStatus = ["active", "trialing"].includes(status);

    return {
      status,
      tier,
      isPremium,
      isActive: isPaidTier || isActiveStatus,
      isTrial: status === "trialing",
      isPastDue: status === "past_due",
      trialDaysLeft: status === "trialing" ? calculateDaysUntil(trialEndDate) : null,
      trialEndDate: trialEndDate || null,
      currentPeriodEnd: client?.subscription?.currentPeriodEnd || null,
      stripeCustomerId: client?.stripeCustomerId || client?.subscription?.stripeCustomerId || null,
      isTesterPremium: false, // placeholder — overridden in testerSubscription
    };
  }, [client?.subscription, client?.subscriptionStatus, client?.subscriptionTier, client?.stripeCustomerId]);

  // Admin tier preview setter — persists to sessionStorage
  const setTierPreview = useCallback((tier) => {
    try {
      if (tier) {
        sessionStorage.setItem("scope180-tier-preview", tier);
      } else {
        sessionStorage.removeItem("scope180-tier-preview");
      }
    } catch { /* ignore */ }
    setTierPreviewState(tier || null);
  }, []);

  // Compute tester premium status from client data
  const testerPremiumActive = useMemo(() => {
    if (!client) return false;
    const isTesterUser = client.subRole === "beta-tester" || client.testerMode === true;
    if (!isTesterUser || client.testerAccessRevoked === true) return false;
    const feedbackCount = client.testerFeedbackCount || 0;
    if (feedbackCount < 1) return false;
    const expiresAt = client.testerPremiumExpiresAt?.toDate?.() ||
      (client.testerPremiumExpiresAt ? new Date(client.testerPremiumExpiresAt) : null);
    return expiresAt && new Date() < expiresAt;
  }, [client]);

  // Override subscription when admin tier preview is set OR tester premium is active
  const effectiveSubscription = useMemo(() => {
    // Admin tier preview (only Free / Premium) — takes highest priority
    if (isAdmin && tierPreview) {
      const isPaid = tierPreview === "premium";
      return {
        ...subscription,
        tier: tierPreview,
        isActive: isPaid,
        isPremium: isPaid,
        isTrial: false,
        isPastDue: false,
        isTesterPremium: false,
        isPreview: true,
      };
    }
    // Tester premium: grant premium access without Stripe subscription
    if (testerPremiumActive && !subscription.isPremium) {
      return {
        ...subscription,
        tier: "premium",
        isActive: true,
        isPremium: true,
        isTesterPremium: true,
      };
    }
    return subscription;
  }, [subscription, isAdmin, tierPreview, testerPremiumActive]);

  // Compute badges state from client data
  const badges = useMemo(() => {
    return {
      achievements: client?.badges?.achievements || [],
      hasAchievement: (badgeId) =>
        (client?.badges?.achievements || []).some(b => b.badgeId === badgeId)
    };
  }, [client?.badges]);

  // Function to refresh client data (useful after subscription changes)
  const refreshClient = useCallback(async () => {
    if (!currentUser?.uid) return;
    try {
      const userRef = doc(db, "users", currentUser.uid);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        setClient(snap.data());
      }
    } catch (err) {
      console.error("Failed to refresh client:", err);
    }
  }, [currentUser?.uid]);

  // Check if user is a specialist (Success Agent) - gets free access to everything
  const isSpecialist = useMemo(() => {
    return roles.includes("specialist") || client?.role === "specialist";
  }, [roles, client?.role]);

  // Check if user is a mentor - also gets free access
  const isMentor = useMemo(() => {
    return roles.includes("mentor") || client?.role === "mentor";
  }, [roles, client?.role]);

  // Check if user is a content manager - gets free access + content/video tools
  const isContentManager = useMemo(() => {
    return roles.includes("content-manager") || client?.role === "content-manager";
  }, [roles, client?.role]);

  // Check if user is an instructor - can grade submissions and manage students
  const isInstructor = useMemo(() => {
    return roles.includes("instructor") || client?.role === "instructor";
  }, [roles, client?.role]);

  // Helper: Check if user has full platform access (admin, specialist, mentor, content-manager, or instructor)
  const hasFullAccess = useMemo(() => {
    return isAdmin || isSpecialist || isMentor || isContentManager || isInstructor;
  }, [isAdmin, isSpecialist, isMentor, isContentManager, isInstructor]);

  /**
   * MEMBERSHIP ACCESS RULES:
   * - Premium Members: Practice exams + Learning tools = FREE, eBooks = PURCHASE
   * - Free Tier: Everything requires individual purchase
   * - Staff (Admin/Specialist/Mentor): Everything = FREE
   */

  // Check if user can access a product type for free based on membership
  const canAccessProductType = useCallback((productType) => {
    // Staff always has free access to everything
    if (hasFullAccess) return true;

    // Premium members get free access to LEARNING TOOLS + EXAMS + SIMULATIONS
    if (effectiveSubscription.isActive) {
      const freeWithPremium = [
        'learning-tool',
        'tool',
        'game',
        'simulator',
        'calculator',
        'mini-lesson',
        'quiz',
        'exam',
        'practice-exam',
        'simulation',
      ];
      return freeWithPremium.includes(productType?.toLowerCase());
    }

    // Free tier users must purchase everything
    return false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFullAccess, effectiveSubscription.isActive]);

  // Determine the access status for a specific product
  const getProductAccess = useCallback((product) => {
    if (!product) return { canAccess: false, reason: 'no-product' };

    const categoryGroup = product.categoryGroup?.toLowerCase() || '';
    const category = product.category?.toLowerCase() || '';
    const type = product.type?.toLowerCase() || '';

    // Determine product type for access rules
    // Practice exams - must be purchased by everyone (except staff)
    const isExam = categoryGroup === 'practice-exam' ||
                   category === 'practice-exam' ||
                   type === 'exam' ||
                   category?.includes('exam');

    // Learning tools - FREE with Premium membership
    const isTool = categoryGroup === 'learning-tool' ||
                   category === 'learning-tool' ||
                   ['game', 'simulator', 'calculator', 'mini-lesson', 'quiz', 'tool'].includes(type) ||
                   ['game', 'simulator', 'calculator', 'mini-lesson', 'quiz'].includes(category);

    // Mini courses - FREE with Premium membership
    const isMiniCourse = categoryGroup === 'mini-course' || category === 'mini-courses';

    // eBooks/digital products - must be purchased by everyone (except staff)
    const isEbook = categoryGroup === 'digital-product' ||
                    category === 'ebook' ||
                    type === 'ebook' ||
                    category?.includes('ebook') ||
                    product.title?.toLowerCase().includes('ebook');

    // Staff always has free access to EVERYTHING
    if (hasFullAccess) {
      return {
        canAccess: true,
        reason: 'staff-access',
        label: 'Staff Access',
        showPrice: false
      };
    }

    // Premium members - learning tools + exams + simulations are free
    if (effectiveSubscription.isActive) {
      // Learning tools are free with premium
      if (isTool) {
        return {
          canAccess: true,
          reason: 'premium-included',
          label: 'Included with Membership',
          showPrice: false
        };
      }

      // Practice exams are free with premium
      if (isExam) {
        return {
          canAccess: true,
          reason: 'premium-included',
          label: 'Included with Membership',
          showPrice: false
        };
      }

      // Mini courses are free with premium
      if (isMiniCourse) {
        return {
          canAccess: true,
          reason: 'premium-included',
          label: 'Included with Membership',
          showPrice: false
        };
      }

      // eBooks require purchase
      if (isEbook) {
        return {
          canAccess: false,
          reason: 'purchase-required',
          label: 'Purchase Required',
          showPrice: true
        };
      }

      // Unknown product type - default to purchase required
      return {
        canAccess: false,
        reason: 'purchase-required',
        label: 'Purchase Required',
        showPrice: true
      };
    }

    // Free tier users - everything requires purchase
    // Show "Free with Premium" for learning tools, practice exams, and mini courses
    const freeWithUpgrade = isTool || isExam || isMiniCourse;
    return {
      canAccess: false,
      reason: 'purchase-required',
      label: freeWithUpgrade ? 'Free with Premium' : 'Purchase Required',
      showPrice: true,
      upgradePrompt: freeWithUpgrade
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFullAccess, effectiveSubscription.isActive]);

  // Check if a product is free with premium membership
  // Learning tools + practice exams + simulations are free with Premium
  const isFreeWithPremium = useCallback((product) => {
    const categoryGroup = product?.categoryGroup?.toLowerCase() || '';
    const category = product?.category?.toLowerCase() || '';
    const type = product?.type?.toLowerCase() || '';

    // Practice exams ARE free with premium
    const isExam = categoryGroup === 'practice-exam' ||
                   category === 'practice-exam' ||
                   type === 'exam' ||
                   category?.includes('exam');

    if (isExam) return true;

    // Mini courses are free with premium
    const isMiniCourse = categoryGroup === 'mini-course' || category === 'mini-courses';
    if (isMiniCourse) return true;

    // Learning tools are free with premium
    const isTool = categoryGroup === 'learning-tool' ||
                   ['game', 'simulator', 'calculator', 'mini-lesson', 'quiz', 'tool', 'simulation'].includes(type) ||
                   ['game', 'simulator', 'calculator', 'mini-lesson', 'quiz', 'simulation'].includes(category);

    return isTool;
  }, []);

  // Compute isTester from client data (mirrors useTesterMode logic)
  const isTester = useMemo(() => {
    if (!client) return false;
    const testerMode = client.subRole === "beta-tester" || client.testerMode === true;
    return testerMode && client.testerAccessRevoked !== true;
  }, [client]);

  const value = {
    currentUser,
    client,
    roles,
    isAdmin,
    isSpecialist,
    isMentor,
    isContentManager,
    isInstructor,
    hasFullAccess,
    isTester,
    loading,
    logout,
    refreshClient,
    // Subscription and badges
    subscription: effectiveSubscription,
    badges,
    // Membership access helpers
    canAccessProductType,
    getProductAccess,
    isFreeWithPremium,
    // AI Quota
    aiQuota: client?.aiQuota || null,
    // Admin tier preview (learner view only)
    tierPreview,
    setTierPreview,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}